import { createElement, escapeRegExp } from "../utils/DOMutils";

// Decode HTML entities Magnolia 6.4 
const decodeHtmlEntities = (text) => {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
};

const getPopupAdsData = () => {
    const mgnlUrl = "https://www.magnolia-cms.com";
    let baseUrl = window.location.href.includes("https://partner.magnolia-cms.com") ? mgnlUrl : ""; // Leave it empty if not on partner portal.
    let language = "";
    const getLanguage = document.querySelector("html").getAttribute("lang");
    const api_languages = [
        {
            lang: "en",
            apiLang: "en",
        },
        {
            lang: "de",
            apiLang: "de_DE",
        },
        {
            lang: "zh",
            apiLang: "zh_CN",
        },
        {
            lang: "es",
            apiLang: "es_ES",
        },
    ];

    const matchedLanguage = api_languages.find((langObj) => langObj.lang === getLanguage);

    if (matchedLanguage) {
        language = `?lang=${matchedLanguage.apiLang}`;
    }

    return fetch(`${baseUrl}/.rest/delivery/popupAds${language}`)
        .then((response) => {
            if (!response.ok) {
                throw new Error("Failed to fetch pop data");
            }
            return response.json();
        })
        .then((data) => {
            return data.results;
        });
};

const getBlogsData = (categories) => {
    const categoriesString = categories.join("%7C");

    return fetch(`/.rest/delivery/blogs?categoriesFilter=${categoriesString}`)
        .then((response) => {
            if (!response.ok) {
                throw new Error("Failed to fetch categories data");
            }
            return response.json();
        })
        .then((data) => {
            return data.results;
        });
};

const modifyURL = (url) => {
    let new_url = url;
    const main_domains = ["partner.magnolia-cms.com", "magnolia-cms.com"];
    const urlsToProcess = ["corp-website", "partner-portal"];
    const languages = ["es_ES", "de_DE", "zh_ZH"];

    // Map testing domains to production domains
    const prodDomainMap = {
        "https://author.prod.corp-webpre.magnolia-platform.com/corpweb": "https://www.magnolia-cms.com",
        "https://author.prod.corp-webpre.magnolia-platform.com/partner-portal":
            "https://partner.magnolia-cms.com",
    };

    // Replace testing environment domains with production domains
    for (const [testDomain, prodDomain] of Object.entries(prodDomainMap)) {
        if (url.startsWith(testDomain)) {
            new_url = url.replace(testDomain, prodDomain);
            break;
        }
    }

    if (new_url.includes("/corpweb")) {
        new_url = new_url.replace("/corpweb", "/corp-website");
    }

    // Ensure the URL is always using https
    new_url = new_url.replace(/^http:\/\//i, "https://");

    // Function to remove the segment if something follows it
    const processURL = (url, segment) => {
        const safeSegment = escapeRegExp(segment);
        const pattern = new RegExp(`/${safeSegment}(?:/|\\.)`);
        if (pattern.test(url)) {
            const afterSegment = url.split(pattern)[1];
            if (afterSegment && afterSegment.length > 0) {
                return url.replace(pattern, "/");
            }
        }
        return url;
    };

    // Process corp-website and partner-portal
    urlsToProcess.forEach((segment) => {
        new_url = processURL(new_url, segment);
    });

    // Match the main domains
    const matchesMainDomain = (url) => {
        return main_domains.some((domain) => {
            let cleanUrl = url.replace(/\/$/, "");
            let domainMatch = cleanUrl.endsWith(domain);
            let languagePathMatch = languages.some((lang) => cleanUrl.endsWith(`${domain}/${lang}`));
            return domainMatch || languagePathMatch;
        });
    };

    // Handling non-main domains
    if (!matchesMainDomain(new_url)) {
        new_url = new_url.replace(/^https?:\/\//, ""); // Remove protocol
        const index = new_url.indexOf("/");
        new_url = index !== -1 ? new_url.substring(index + 1) : "";
        new_url = new_url.replace(/\.html$|\/$/, ""); // Remove .html or trailing slash
    }

    if (new_url === "corp-website" || new_url === "https://www.magnolia-cms.com/") {
        new_url = "https://www.magnolia-cms.com";
    } else if (
        new_url === "partner-portal" ||
        new_url === "https://partner.magnolia-cms.com/" ||
        new_url === "https://partnerportal.magnolia-cms.com/"
    ) {
        new_url = "https://partner.magnolia-cms.com";
    }

    return new_url;
};

const targetPage = (data, pages) => {
    let currentURL = window.location.href.split("?")[0];
    // for testing
    // let currentURL = "https://partner.magnolia-cms.com"
    if (!currentURL.includes("https://www.magnolia-cms.com")) {
        currentURL = currentURL.replace("/corpweb", "/corp-website");
    }

    let filteredAds = data;

    let blogsUrl,
        filteredPages = "";
    if (currentURL.includes("blog")) {
        const startIndex = currentURL.lastIndexOf("/") + 1;
        const endIndex = currentURL.indexOf(".html");
        blogsUrl = currentURL.substring(startIndex, endIndex);

        filteredPages = pages.filter((page) => page["@name"] === blogsUrl);

        const categoryIdLookup = new Set();

        data.forEach((item) => {
            if (item.targetPages) {
                Object.values(item.targetPages).forEach((page) => {
                    const pageCategory = page.pageCategory;
                    if (pageCategory && pageCategory["@id"]) {
                        categoryIdLookup.add(pageCategory["@id"].toString()); // Convert ID to string
                    }
                });
            }
        });

        const filteredCategories = filteredPages.map((page) => {
            const matchingCategories = page.categoriesFilter.filter((category) =>
                categoryIdLookup.has(category["@id"].toString())
            );

            const lastMatchedCategory =
                matchingCategories.length > 0 ? matchingCategories[matchingCategories.length - 1] : null;

            return lastMatchedCategory;
        });

        filteredAds = [];
        data.forEach((newItem) => {
            if (newItem && newItem.targetPages) {
                const targetPagesNodes = newItem.targetPages["@nodes"];

                targetPagesNodes.forEach((targetPageKey) => {
                    const adItem = newItem.targetPages[targetPageKey];
                    const matchingCategory = filteredCategories.find((filteredCategory) => {
                        return adItem.pageCategory && adItem.pageCategory["@id"] === filteredCategory["@id"];
                    });

                    if (matchingCategory) {
                        filteredAds.push(newItem);
                    }
                });
            }
        });
    }

    currentURL = modifyURL(currentURL);

    const allDynamicAds = filteredAds.map((item, i) => {
        const dynamicAd = {
            adItem: item,
            singlePages: [],
            pageTree: [],
            pageCategories: [],
            index: i,
            name: item["@name"],
        };
        const ad = item.targetPages;
        if (ad && ad["@nodes"].length > 0) {
            for (const page of ad["@nodes"]) {
                if (
                    ad[page].singlePage &&
                    ad[page].field === "singlePage" &&
                    /https?:\/\//.test(ad[page].singlePage)
                ) {
                    dynamicAd.singlePages.push(modifyURL(ad[page].singlePage));
                } else if (
                    ad[page].pageTree &&
                    ad[page].field === "pageTree" &&
                    /https?:\/\//.test(ad[page].pageTree)
                ) {
                    dynamicAd.pageTree.push(modifyURL(ad[page].pageTree));
                } else if (ad[page].pageCategory && ad[page].field === "pageCategory") {
                    dynamicAd.pageCategories.push(ad[page].pageCategory);
                    break;
                }
            }
        }
        return dynamicAd;
    });

    // console.log(allDynamicAds)
    for (const dynamicAd_objc of allDynamicAds) {
        // console.log(dynamicAd_objc)
        const pageTree_match = dynamicAd_objc.pageTree.some((prefix) => currentURL.startsWith(prefix));
        const singlePage_match = dynamicAd_objc.singlePages.some((url) => currentURL === url);
        let matchesURL = false;

        if (dynamicAd_objc.pageCategories.length > 0 && pages && pages.length > 0) {
            const pages_matched_by_cats = filterByCats(dynamicAd_objc.pageCategories, pages);

            matchesURL = pages_matched_by_cats.some((page) => {
                const pageURL = "blog/" + page["@name"].toLowerCase();
                return currentURL === pageURL;
            });
        }

        if (pageTree_match || singlePage_match || matchesURL) {
            return dynamicAd_objc.adItem;
        }
    }
    return null;
};

const createAd = (data) => {
    if (!data) {
        return;
    }

    const mainEl = document.getElementsByTagName("main")[0];

    const wrapper = document.createElement("div");
    const textContent = document.createElement("div");
    const img_w = document.createElement("div");
    const img_el = document.createElement("img");
    const title_w = document.createElement("span");
    const buttonLabel = document.createElement("a");

    const is_bar = data?.layout_type?.field === "bar_layout";

    if (is_bar) {
        document.querySelector(".nav.js-module-nav").classList.add("nav--has-bar");
        // const category =  document.createElement("span");
        // category.textContent = data.categories_visible?.displayName;
        // category.classList.add("tag");
        // textContent.appendChild(category);
        wrapper.classList.add("ads-w--bar");
    }

    wrapper.classList.add("ads-w");

    textContent.classList.add("ads-w__content");
    img_w.classList.add("ads-w__img");
    title_w.className = "ads-w__content__title ads-w__title";
    buttonLabel.className = "link-button link-button link-button--full";

    let buttonLink = (data.link && (data.link.internal || data.link.external)) || "/";
    buttonLink = buttonLink.replace(
        /https?:\/\/partnerportal\.magnolia-cms\.com\//,
        "https://partner.magnolia-cms.com/"
    );
    buttonLabel.href = buttonLink;

    if (data.linkTrackingName) {
        wrapper.dataset.trackingName = data.linkTrackingName;
    }

    if (data.image && data.image["@link"] && !is_bar) {
        wrapper.appendChild(img_w);
        img_w.appendChild(img_el);
        img_el.src = data.image["@link"];
    }

    wrapper.appendChild(textContent);

    if (data.theme) {
        wrapper.classList.add(`ads-w--${data.theme}`);
        wrapper.classList.add(`theme-${data.theme}`);
    }

    if (data.title) {
        title_w.innerHTML = decodeHtmlEntities(data.title);
        if (is_bar) {
            title_w.style.marginRight = "2rem";
        }
        textContent.appendChild(title_w);
    }

    if (data.linkLabel) {
        buttonLabel.innerText = data.linkLabel;
        buttonLabel.style.fontSize = "1.1rem";
        textContent.appendChild(buttonLabel);
    }

    if (is_bar) {
        const removeBarBtn = createElement("button", {
            class: "remove-bar",
            type: "button",
            "aria-label": "Close advertisement banner",
            title: "Close advertisement banner",
        });
        removeBarBtn.innerHTML =
            '<svg width="14" height="15" viewBox="0 0 14 15" fill="none" xmlns="http://www.w3.org/2000/svg">\n' +
            '<path d="M6.85714 5.58314L12.1907 0L13.7143 1.59486L8.38071 7.178L13.7143 12.7611L12.1907 14.356L6.85714 8.77286L1.52357 14.356L0 12.7611L5.33357 7.178L0 1.59486L1.52357 0L6.85714 5.58314Z" fill="white"/>\n' +
            "</svg>";
        textContent.appendChild(removeBarBtn);

        const nav = document.querySelector(".nav__main");
        if (nav) {
            const headerEl = document.querySelector("header.hero");
            const signature = nav.querySelector(".sg-w");

            const setOffset = () => {
                if (!headerEl) {
                    return;
                }

                if (window.innerWidth > 672 && window.innerWidth < 1271) {
                    if (signature) {
                        headerEl.style.marginTop = "85px";
                    }
                } else {
                    if (signature) {
                        headerEl.style.marginTop = "96px";
                    }
                }
            };

            window.addEventListener("resize", setOffset);
            setOffset();

            nav.style.top = "70px";

            removeBarBtn.onclick = (event) => {
                event.preventDefault();
                hideBar(wrapper);
                nav.style.top = "0px";
                nav.querySelector(".sg-w")
                    ? (document.querySelector("header.hero").style.marginTop = "0")
                    : "";
            };
        }
    }

    mainEl.appendChild(wrapper);

    const hideBarOnScroll = () => {
        const nav = document.querySelector(".nav.js-module-nav");
        const navMain = nav.querySelector(".nav__main");
        const scrollY = window.scrollY;

        let initialTop = 70;

        const setBarPosition = () => {
            const newTop = Math.max(0, initialTop - scrollY); // Ensure it does not go below 0
            navMain.style.top = `${newTop}px`;
            nav.style.top = `${newTop}px`;
        };

        if (nav && nav.classList.contains("nav--has-bar")) {
            if (window.innerWidth > 672) {
                setBarPosition();
            } else {
                navMain.style.top = 0;
                nav.style.top = 0;
                nav.classList.remove("nav--has-bar");
            }

            window.addEventListener("resize", () => {
                if (window.innerWidth > 672) {
                    setBarPosition();
                    nav.classList.add("nav--has-bar");
                } else {
                    navMain.style.top = 0;
                    nav.style.top = 0;
                    nav.classList.remove("nav--has-bar");
                    nav.querySelector(".sg-w")
                        ? (document.querySelector("header.hero").style.marginTop = "0")
                        : "";
                }
            });
        }
    };

    if (!is_bar) {
        const visible_treshold = 1900;
        toggleElementVisibility(visible_treshold, "popup")();
        window.addEventListener("resize", toggleElementVisibility(visible_treshold, "popup"));
    } else {
        const visible_treshold = 672;
        toggleElementVisibility(visible_treshold, "bar")();
        window.addEventListener("resize", toggleElementVisibility(visible_treshold, "bar"));
        hideBarOnScroll();
        window.addEventListener("scroll", hideBarOnScroll);
    }
};

const footer = document.querySelector("footer");

const scrollHandler = () => {
    const element = document.getElementsByClassName("ads-w")[0];
    const elemRect = footer.getBoundingClientRect();
    const footerPosition = elemRect.top + window.pageYOffset - footer.offsetHeight;
    if (window.scrollY > 200 && window.scrollY < footerPosition + 400) {
        if (element) element.style.opacity = 1;
    } else {
        if (element) element.style.opacity = 0;
    }
};

const toggleElementVisibility = (maxWidth, type) => {
    return () => {
        const elementToHide = document.querySelector(".ads-w");
        if (window.innerWidth < maxWidth) {
            if (elementToHide) elementToHide.style.display = "none";
        } else {
            if (type === "popup") {
                window.addEventListener("scroll", scrollHandler);
            }
            if (elementToHide) elementToHide.style.display = "flex";
        }
    };
};

const filterByCats = (cats, pages) => {
    const categoriesFilterNames = cats.map((category) => category["@name"]);
    return pages.filter(
        (page) =>
            page.categoriesFilter &&
            page.categoriesFilter.some((category) => categoriesFilterNames.includes(category["@name"]))
    );
};

const typeCategories = (data) => {
    const adCategories = [];
    data.forEach((item) => {
        const ad = item.targetPages;
        if (ad && ad["@nodes"].length > 0) {
            ad["@nodes"].forEach((page) => {
                if (ad[page].pageCategory && ad[page].field === "pageCategory") {
                    adCategories.push({
                        adItem: ad,
                        adCategory: ad[page].pageCategory,
                    });
                }
            });
        }
    });

    return adCategories;
};

const hideBar = (bar) => {
    if (bar) {
        document.querySelector(".js-module-nav").classList.remove("nav--has-bar");
        addHiddenBar();
        bar.style.display = "none";
    }
};

const addHiddenBar = () => {
    const currentUrl = window.location.href;
    let hiddenBars = JSON.parse(sessionStorage.getItem("hiddenBars")) || [];

    if (!hiddenBars.includes(currentUrl)) {
        hiddenBars.push(currentUrl);
        sessionStorage.setItem("hiddenBars", JSON.stringify(hiddenBars));
    }
};

const checkHiddenBar = () => {
    const currentUrl = window.location.href;
    let hiddenBars = JSON.parse(sessionStorage.getItem("hiddenBars")) || [];

    return hiddenBars.includes(currentUrl);
};

export default getPopupAdsData;
const blackListUrls = ["video.magnolia-cms.com"];
const siteUrl = window.location.href;
const isBlacklisted = blackListUrls.some((url) => siteUrl.includes(url));

!isBlacklisted && !checkHiddenBar()
    ? getPopupAdsData()
          .then(async (adsData) => {
              try {
                  if (adsData.length > 0) {
                      if (window.location.href.includes("blog")) {
                          const categories = typeCategories(adsData);
                          const cats = [];

                          for (const category of categories) {
                              cats.push(category.adCategory["@id"]);
                          }

                          const blogsData = await getBlogsData(cats);
                          if (blogsData) {
                              createAd(targetPage(adsData, blogsData));
                          }
                      } else {
                          createAd(targetPage(adsData, []));
                      }
                  }
              } catch (innerError) {
                  console.error("An error occurred while processing the ad data or blogs data:", innerError);
              }
          })
          .catch((error) => {
              console.error("An error occurred during the initial fetching of ads data:", error);
          })
    : null;
